import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  DataTable,
  EmptyState,
  ErrorNotice,
  LoadingState,
  Panel,
  SearchInput,
  useDebouncedValue,
} from '@bcecloud/portal-ui';
import { getJson, sendJson, errorMessage } from '@/lib/api';
import type {
  LinkedSalesforceAccount,
  LinkedSalesforceAccounts,
  SalesforceAccountHit,
} from '@/lib/types';

export interface SalesforceAccountsPanelProps {
  readonly organizationId: string;
  readonly organizationName: string;
}

/** How many child accounts a row names before folding the rest into a count. */
const CHILDREN_SHOWN = 4;

/** The ids the organization stores, derived from the linked rows. */
function idsOf(rows: readonly LinkedSalesforceAccount[]): {
  salesforceAccountIds: string[];
  salesforceParentAccountIds: string[];
} {
  return {
    salesforceAccountIds: rows.filter((row) => !row.includesChildren).map((row) => row.id),
    salesforceParentAccountIds: rows.filter((row) => row.includesChildren).map((row) => row.id),
  };
}

/**
 * Which Salesforce accounts belong to a client organization — the join that
 * puts delivered events in their portal.
 *
 * Staff search Salesforce by name and link the accounts that belong. A linked
 * account can also be marked to include its child accounts, so a national
 * parent brings every region in without each being listed; the children are
 * read from Salesforce when the join is used, so a new region shows up on its
 * own.
 *
 * @param props - The organization.
 * @returns The panel.
 */
export function SalesforceAccountsPanel({
  organizationId,
  organizationName,
}: SalesforceAccountsPanelProps): React.ReactElement {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const debounced = useDebouncedValue(search, 250).trim();

  const linked = useQuery({
    queryKey: ['salesforce-accounts', organizationId],
    queryFn: ({ signal }) =>
      getJson<LinkedSalesforceAccounts>(
        `/organizations/${organizationId}/salesforce-accounts`,
        undefined,
        signal,
      ),
  });

  const hits = useQuery({
    queryKey: ['salesforce-account-search', debounced],
    queryFn: ({ signal }) =>
      getJson<SalesforceAccountHit[]>(
        '/organizations/salesforce-accounts',
        { q: debounced },
        signal,
      ),
    enabled: debounced.length >= 2,
  });

  const save = useMutation({
    mutationFn: (rows: readonly LinkedSalesforceAccount[]) =>
      sendJson('patch', `/organizations/${organizationId}`, idsOf(rows)),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['salesforce-accounts', organizationId],
        }),
        queryClient.invalidateQueries({
          queryKey: ['organization', organizationId],
        }),
      ]);
    },
  });

  if (linked.isPending) return <LoadingState label="Loading linked accounts…" />;
  if (linked.isError) {
    return <ErrorNotice title="Linked accounts unavailable" message={errorMessage(linked.error)} />;
  }

  const rows = linked.data.accounts;
  const linkedIds = new Set(rows.map((row) => row.id));
  const candidates = (hits.data ?? []).filter((hit) => !linkedIds.has(hit.id));
  const unreachable = !linked.data.salesforceReachable;

  function link(hit: SalesforceAccountHit): void {
    save.mutate([...rows, { ...hit, includesChildren: false, children: [] }]);
  }

  function setIncludesChildren(id: string, includesChildren: boolean): void {
    save.mutate(rows.map((row) => (row.id === id ? { ...row, includesChildren } : row)));
  }

  function unlink(id: string): void {
    save.mutate(rows.filter((row) => row.id !== id));
  }

  return (
    <div>
      <Panel className="p-5">
        <label htmlFor="salesforce-account-search" className="block text-portal-tiny font-bold">
          Link a Salesforce account
        </label>
        <div className="mt-1">
          <SearchInput
            id="salesforce-account-search"
            label="Search Salesforce accounts"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={`Search Salesforce for ${organizationName}…`}
          />
        </div>

        {debounced.length < 2 ? null : hits.isPending ? (
          <p className="mt-2 text-portal-small text-portal-muted">Searching Salesforce…</p>
        ) : hits.isError ? (
          <div className="mt-2">
            <ErrorNotice inline message={errorMessage(hits.error)} />
          </div>
        ) : candidates.length === 0 ? (
          <p className="mt-2 text-portal-small text-portal-muted">
            No accounts match — or every match is already linked.
          </p>
        ) : (
          <ul className="mt-2 divide-y divide-portal-line rounded-portal-card border border-portal-line">
            {candidates.map((hit) => (
              <li key={hit.id} className="flex items-center justify-between gap-3 px-3 py-2">
                <span className="min-w-0">
                  <span className="block text-portal-small font-bold">{hit.name}</span>
                  <span className="block text-portal-micro text-portal-muted">{describe(hit)}</span>
                </span>
                <Button variant="secondary" disabled={save.isPending} onClick={() => link(hit)}>
                  Link
                </Button>
              </li>
            ))}
          </ul>
        )}
        {save.isError ? (
          <div className="mt-2">
            <ErrorNotice inline title="Could not save" message={errorMessage(save.error)} />
          </div>
        ) : null}
      </Panel>

      <div className="mt-6">
        {unreachable ? (
          <ErrorNotice
            inline
            message="Salesforce could not be reached, so account names and child accounts are missing. The links themselves still work."
          />
        ) : null}

        {rows.length === 0 ? (
          <Panel>
            <EmptyState
              icon="⛓"
              title="No Salesforce accounts linked"
              message={`Delivered events reach ${organizationName}'s portal through this link. Search above and link the accounts that belong to them.`}
            />
          </Panel>
        ) : (
          <DataTable
            headers={['Account', 'Parent account', 'Child accounts', 'Actions']}
            visuallyHiddenHeaders={[3]}
          >
            {rows.map((row) => (
              <tr key={row.id} className="border-t border-portal-line align-top text-portal-tiny">
                <td className="px-4 py-2.5">
                  <span className="block text-portal-small font-bold">{row.name}</span>
                  <span className="block text-portal-micro text-portal-muted">{describe(row)}</span>
                </td>
                <td className="px-4 py-2.5 text-portal-muted">{row.parent?.name ?? '—'}</td>
                <td className="px-4 py-2.5">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={row.includesChildren}
                      disabled={save.isPending}
                      onChange={(event) => setIncludesChildren(row.id, event.target.checked)}
                    />
                    Include child accounts
                  </label>
                  {row.includesChildren ? (
                    <span className="mt-1 block text-portal-micro text-portal-muted">
                      {childrenSummary(row.children)}
                    </span>
                  ) : null}
                </td>
                <td className="px-4 py-2.5 text-right whitespace-nowrap">
                  <button
                    type="button"
                    disabled={save.isPending}
                    onClick={() => unlink(row.id)}
                    className="cursor-pointer text-portal-micro font-semibold text-brand hover:underline disabled:cursor-default disabled:opacity-50"
                  >
                    Unlink
                  </button>
                </td>
              </tr>
            ))}
          </DataTable>
        )}
      </div>
    </div>
  );
}

/** "Customer · New York, NY", or the id when Salesforce gave no details. */
function describe(account: SalesforceAccountHit): string {
  const parts = [account.type, account.billing].filter((part): part is string => Boolean(part));
  return parts.length > 0 ? parts.join(' · ') : account.id;
}

/** "3 child accounts: KPMG East, KPMG West, KPMG Central". */
function childrenSummary(children: LinkedSalesforceAccount['children']): string {
  if (children.length === 0) return 'No child accounts in Salesforce yet.';
  const named = children.slice(0, CHILDREN_SHOWN).map((child) => child.name);
  const more = children.length - named.length;
  const count = `${children.length} child account${children.length === 1 ? '' : 's'}`;
  return `${count}: ${named.join(', ')}${more > 0 ? ` and ${more} more` : ''}`;
}
